import { AxiosResponse } from 'axios';
import client from 'api/client';
import type {
    TodayData,
    TeamHealthData,
    PractitionerSpeedsData,
    PractitionerDocumentUploadsData,
    ApplicantDocumentUploadsData,
    PractitionerMessagesPostedData,
    ApplicantPostedMessagesData,
    ServicePriceHistogramData,
    AnalyticsRoleType,
    ConversionData,
    RevenueData,
    OperationsData,
    FunnelDropOffItem,
    AgingDrillDownItem,
    PractitionerCaseItem,
    ServiceProviderDailyActivityData,
    ServiceProviderDailyActivityResponse,
    ServiceProviderMonthlyComparisonData,
    ClientStatusData,
    BackofficeDocumentUploadsSummaryData,
    BackofficeDocumentUploadsDrilldownData,
    DocumentStatusMovesSummaryData,
    DocumentStatusMovesDrilldownData,
    StatusMovementsData,
    StatusMovementsRequest,
    AssessmentCompletionTimesData,
} from 'hooks/analytics';

// Response interfaces matching backend endpoints
export interface AnalyticsKPIsResponse {
    totalApplications: number;
    revenue: {
        gross: number;
        net: number;
        refunds: number;
    };
    conversionRate: {
        leadToPaid: number;
    };
    avgProcessingTime: number;
    pipelineValue: number;
    abandonmentRate: number;
}

export interface ApplicationStatusResponse {
    applicationsByStatus: {
        draft: number;
        submitted: number;
        inProgress: number;
        closed: number;
    };
}

export interface ConversionFunnelResponse {
    conversionRate: {
        leadToPaid: number;
        paidToSubmitted: number;
        submittedToApproved: number;
    };
}

export interface LeadPipelineResponse {
    leads: {
        new: number;
        converted: number;
        offersSent: number;
        completedAssessments: number;
    };
}

export interface TopServicesResponse {
    topServices: Array<{
        name: string;
        volume: number;
        revenue: number;
    }>;
}

export interface BookingsByServiceResponse {
    bookingsByService: Array<{
        service: string;
        count: number;
    }>;
}

export interface ClientVolumeByCountryResponse {
    volumeByCountry: Array<{
        country: string;
        count: number;
    }>;
}

export interface ClientMetricsResponse {
    repeatCustomers: number;
    avgOrderValue: number;
    cancellationRate: number;
}

export interface CaseOfficerWorkloadResponse {
    caseOfficerWorkload: {
        assigned: number;
        inProgress: number;
        overdue: number;
    };
}

export interface QualityMetricsResponse {
    slaBreaches: number;
    documentCompleteness: number;
    reworkRate: number;
}

export interface SalesByClientLocationResponse {
    salesByClientLocation: Array<{
        location: string;
        amount: number;
    }>;
}

export interface AlertsResponse {
    alerts: Array<{
        type: 'warning' | 'danger' | 'info';
        title: string;
        message: string;
    }>;
}

export interface SoldValueDistributionRequest {
    from?: string;
    to?: string;
    company_id?: string;
    service_ids?: number[];
    program_names?: string[];
    bucket_count?: number;
    range_min?: number;
    range_max?: number;
    currency?: string;
    status?: string;
}

export type AnalyticsServiceOption = string;

export interface AdminCompaniesResponse {
    [serviceProviderId: string]: string;
}

export interface AnalyticsCountResponse {
    count?: number;
    total?: number;
    messages_posted?: number;
    documents_uploaded?: number;
}

export interface MessagesPostedResponse {
    summary: {
        active_cases: number;
        messages_posted: number;
    };
    top_message_posters: Array<{
        candidate_name: string;
        case_id: number;
        messages_posted: number;
    }>;
}

export interface DocumentsUploadedResponse {
    summary: {
        active_cases: number;
        documents_uploaded: number;
    };
    top_document_uploaders: Array<{
        candidate_name: string;
        case_id: number;
        documents_uploaded: number;
    }>;
}

export interface SoldValueDistributionResponse {
    filters: {
        from?: string | null;
        to?: string | null;
        company_id?: number | string | null;
        service_ids?: number[];
        bucket_count?: number;
        range_min?: number | null;
        range_max?: number | null;
        currency?: string | null;
        status?: string;
        filter_mode?: string;
        program_names?: string[];
    };
    summary: {
        total_sold_cases: number;
        total_sold_value: number;
        average_sold_value: number;
        median_sold_value: number;
        min_sold_value: number | null;
        max_sold_value: number | null;
    };
    distribution: {
        bucket_strategy: string;
        bucket_count: number;
        buckets: Array<{
            index: number;
            min_value: number;
            max_value: number;
            count: number;
            total_value?: number;
        }>;
    };
    range_query: {
        range_min: number | null;
        range_max: number | null;
        count: number;
        total_value?: number;
    } | null;
    program_names_included?: string[];
    services_included: Array<{
        id: number;
        name: string;
    }>;
}

// Helper function to build params
const buildParams = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Record<string, string> => {
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (serviceProviderId) params.service_provider_id = serviceProviderId;
    if (service) params.service = service;
    if (status) params.status = status;
    return params;
};

// Analytics API endpoints
export const getKPIs = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<AnalyticsKPIsResponse>> => {
    return client.get<AnalyticsKPIsResponse>('/analytics/kpis', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getApplicationStatus = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<ApplicationStatusResponse>> => {
    return client.get<ApplicationStatusResponse>('/analytics/application-status', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

/** Per-status counts of apps that entered each client_status in the date range */
export const getClientStatus = (
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<ClientStatusData>> => {
    return client.get<ClientStatusData>('/analytics/client-status', {
        params: buildParams(from, to, serviceProviderId),
    });
};

export const getConversionFunnel = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<ConversionFunnelResponse>> => {
    return client.get<ConversionFunnelResponse>('/analytics/conversion-funnel', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getLeadPipeline = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<LeadPipelineResponse>> => {
    return client.get<LeadPipelineResponse>('/analytics/lead-pipeline', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getTopServices = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<TopServicesResponse>> => {
    return client.get<TopServicesResponse>('/analytics/top-services', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getBookingsByService = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<BookingsByServiceResponse>> => {
    return client.get<BookingsByServiceResponse>('/analytics/bookings-by-service', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getClientVolumeByCountry = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<ClientVolumeByCountryResponse>> => {
    return client.get<ClientVolumeByCountryResponse>('/analytics/client-volume-by-country', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getClientMetrics = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<ClientMetricsResponse>> => {
    return client.get<ClientMetricsResponse>('/analytics/client-metrics', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getCaseOfficerWorkload = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<CaseOfficerWorkloadResponse>> => {
    return client.get<CaseOfficerWorkloadResponse>('/analytics/case-officer-workload', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getQualityMetrics = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<QualityMetricsResponse>> => {
    return client.get<QualityMetricsResponse>('/analytics/quality-metrics', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getSalesByClientLocation = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<SalesByClientLocationResponse>> => {
    return client.get<SalesByClientLocationResponse>('/analytics/sales-by-client-location', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getAlerts = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    service?: string,
    status?: string
): Promise<AxiosResponse<AlertsResponse>> => {
    return client.get<AlertsResponse>('/analytics/alerts', {
        params: buildParams(from, to, serviceProviderId, service, status),
    });
};

export const getMessagesPosted = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    status?: string
): Promise<AxiosResponse<MessagesPostedResponse>> => {
    return client.get<MessagesPostedResponse>('/analytics/messages-posted', {
        params: {
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
            ...(status ? { status } : {}),
            ...(serviceProviderId ? { service_provider_id: serviceProviderId } : {}),
        },
    });
};

export const getDocumentsUploaded = (
    from?: string,
    to?: string,
    serviceProviderId?: string,
    status?: string
): Promise<AxiosResponse<DocumentsUploadedResponse>> => {
    return client.get<DocumentsUploadedResponse>('/analytics/documents-uploaded', {
        params: {
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
            ...(status ? { status } : {}),
            ...(serviceProviderId ? { service_provider_id: serviceProviderId } : {}),
        },
    });
};

export const getAnalyticsServices = (
    serviceProviderId?: string
): Promise<AxiosResponse<AnalyticsServiceOption[]>> => {
    return client.get<AnalyticsServiceOption[]>('/analytics/services', {
        params: serviceProviderId ? { service_provider_id: serviceProviderId } : undefined,
    });
};

export const getSoldValueDistribution = (
    payload: SoldValueDistributionRequest
): Promise<AxiosResponse<SoldValueDistributionResponse>> => {
    return client.post<SoldValueDistributionResponse>(
        '/analytics/sold-value-distribution',
        payload
    );
};

export const downloadAdminAnalyticsReport = (
    serviceProviderId: string,
    from: string,
    to: string
): Promise<AxiosResponse<Blob>> => {
    return client.get<Blob>('/report', {
        params: {
            service_provider_id: serviceProviderId,
            from,
            to,
        },
        responseType: 'blob',
    });
};

export const downloadManagementReport = (
    from: string,
    to: string
): Promise<AxiosResponse<Blob>> => {
    return client.get<Blob>('/analytics/v2/management-report.pdf', {
        params: { from, to },
        responseType: 'blob',
    });
};

// ============================================================
// Analytics v2 — Phase 3 endpoints
// ============================================================

export const getTodayAnalytics = (
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<TodayData>> => {
    return client.get<TodayData>('/analytics/v2/today', {
        params: buildParams(from, to, serviceProviderId),
    });
};

export const getTeamHealth = (
    roleType: AnalyticsRoleType,
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<TeamHealthData>> => {
    return client.get<TeamHealthData>('/analytics/v2/team-health', {
        params: { role_type: roleType, ...buildParams(from, to, serviceProviderId) },
    });
};

export const getPractitionerSpeeds = (
    roleType: AnalyticsRoleType,
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<PractitionerSpeedsData>> => {
    return client.get<PractitionerSpeedsData>('/analytics/v2/practitioner-speeds', {
        params: { role_type: roleType, ...buildParams(from, to, serviceProviderId) },
    });
};

export const getPractitionerDocumentUploads = (
    roleType: AnalyticsRoleType,
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<PractitionerDocumentUploadsData>> => {
    return client.get<PractitionerDocumentUploadsData>(
        '/analytics/v2/practitioner-document-uploads',
        {
            params: { role_type: roleType, ...buildParams(from, to, serviceProviderId) },
        }
    );
};

export const getApplicantDocumentUploads = (
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<ApplicantDocumentUploadsData>> => {
    return client.get<ApplicantDocumentUploadsData>('/analytics/v2/applicant-document-uploads', {
        params: buildParams(from, to, serviceProviderId),
    });
};

export const getPractitionerMessagesPosted = (
    roleType: AnalyticsRoleType,
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<PractitionerMessagesPostedData>> => {
    return client.get<PractitionerMessagesPostedData>(
        '/analytics/v2/practitioner-messages-posted',
        {
            params: { role_type: roleType, ...buildParams(from, to, serviceProviderId) },
        }
    );
};

export const getApplicantPostedMessages = (
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<ApplicantPostedMessagesData>> => {
    return client.get<ApplicantPostedMessagesData>('/analytics/v2/applicant-posted-messages', {
        params: buildParams(from, to, serviceProviderId),
    });
};

export const getConversionAnalytics = (
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<ConversionData>> => {
    return client.get<ConversionData>('/analytics/v2/conversion', {
        params: buildParams(from, to, serviceProviderId),
    });
};

export const getRevenueAnalytics = (
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<RevenueData>> => {
    return client.get<RevenueData>('/analytics/v2/revenue', {
        params: buildParams(from, to, serviceProviderId),
    });
};

export const getOperationsAnalytics = (
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<OperationsData>> => {
    return client.get<OperationsData>('/analytics/v2/operations', {
        params: buildParams(from, to, serviceProviderId),
    });
};

export interface AssessmentCompletionTimesRequest {
    from: string;
    to: string;
    /** Narrows the returned `selection` to one agent's journeys. */
    agentId?: number;
    serviceProviderId?: string;
}

export const getAssessmentCompletionTimes = ({
    from,
    to,
    agentId,
    serviceProviderId,
}: AssessmentCompletionTimesRequest): Promise<AxiosResponse<AssessmentCompletionTimesData>> => {
    return client.get<AssessmentCompletionTimesData>('/analytics/v2/assessment-completion-times', {
        params: {
            from,
            to,
            ...(agentId != null ? { agentId } : {}),
            ...(serviceProviderId ? { service_provider_id: serviceProviderId } : {}),
        },
    });
};

export const getStatusMovements = ({
    from,
    to,
    serviceProviderId,
    includeStatuses = [],
    excludeStatuses = [],
    page = 1,
    pageSize = 10,
}: StatusMovementsRequest): Promise<AxiosResponse<StatusMovementsData>> => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (serviceProviderId) params.append('service_provider_id', serviceProviderId);
    includeStatuses.forEach((status) => params.append('includeStatuses', status));
    excludeStatuses.forEach((status) => params.append('excludeStatuses', status));
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));

    return client.get<StatusMovementsData>('/analytics/status-movements', {
        params,
    });
};

export const getFunnelDrillDown = (
    transition: string,
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<FunnelDropOffItem[]>> => {
    return client.get<FunnelDropOffItem[]>('/analytics/v2/drilldown/funnel', {
        params: { transition, ...buildParams(from, to, serviceProviderId) },
    });
};

export const getAgingDrillDown = (
    type: string,
    bucketIndex: number,
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<AgingDrillDownItem[]>> => {
    return client.get<AgingDrillDownItem[]>('/analytics/v2/drilldown/aging', {
        params: { type, bucket_index: bucketIndex, ...buildParams(from, to, serviceProviderId) },
    });
};

export const getPractitionerCases = (
    practitionerId: number,
    roleType: AnalyticsRoleType,
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<PractitionerCaseItem[]>> => {
    return client.get<PractitionerCaseItem[]>('/analytics/v2/drilldown/practitioner-cases', {
        params: {
            practitioner_id: practitionerId,
            role_type: roleType,
            ...buildParams(from, to, serviceProviderId),
        },
    });
};

export const getServicePriceHistogram = (
    serviceNames: string[],
    from?: string,
    to?: string,
    serviceProviderId?: string,
    bucketCount?: number
): Promise<AxiosResponse<ServicePriceHistogramData>> => {
    // Use URLSearchParams so service_names is repeated
    // (?service_names=Foo&service_names=Bar) rather than relying on axios's
    // array-serializer default.
    const params = new URLSearchParams();
    serviceNames.forEach((name) => params.append('service_names', name));
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (serviceProviderId) params.append('service_provider_id', serviceProviderId);
    if (bucketCount != null) params.append('bucket_count', String(bucketCount));
    return client.get<ServicePriceHistogramData>('/analytics/v2/service-price-histogram', {
        params,
    });
};

// SUPER_SUPER_ADMIN only. Returns monthly counts (completed assessments,
// fully paid applications, submitted applications) for every active service
// provider over the last 6 calendar months. The window is computed
// server-side; there is no from/to override.
export const getServiceProviderMonthlyComparison = (
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<ServiceProviderMonthlyComparisonData>> => {
    return client.get<ServiceProviderMonthlyComparisonData>(
        '/analytics/v2/service-provider-monthly-comparison',
        { params: buildParams(from, to, serviceProviderId) }
    );
};

export type ServiceProviderDailyActivityRange =
    | { days: 7 | 30 | 90 }
    | { from: string; to: string };

const normalizeServiceProviderDailyActivity = (
    payload: ServiceProviderDailyActivityResponse
): ServiceProviderDailyActivityData => {
    if (Array.isArray(payload)) return payload;

    return payload.providers ?? payload.serviceProviders ?? payload.activity ?? payload.data ?? [];
};

// SUPER_SUPER_ADMIN only. Daily, zero-filled activity metrics for every active
// service provider. Use either a preset number of days or a custom date range.
export const getServiceProviderDailyActivity = (
    range: ServiceProviderDailyActivityRange = { days: 30 }
): Promise<AxiosResponse<ServiceProviderDailyActivityData>> => {
    const params = 'days' in range ? { days: range.days } : { from: range.from, to: range.to };

    return client
        .get<ServiceProviderDailyActivityResponse>(
            '/analytics/v2/service-provider-daily-activity',
            { params }
        )
        .then((response) => ({
            ...response,
            data: normalizeServiceProviderDailyActivity(response.data),
        }));
};

// ============================================================
// Backoffice Document Uploads
// ============================================================

/** Summary: uploads per agent */
export const getBackofficeDocumentUploads = (
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<BackofficeDocumentUploadsSummaryData>> => {
    return client.get<BackofficeDocumentUploadsSummaryData>(
        '/analytics/v2/backoffice-document-uploads',
        { params: buildParams(from, to, serviceProviderId) }
    );
};

/** Drilldown: per-document details for one agent */
export const getBackofficeDocumentUploadsByAgent = (
    agentId: number,
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<BackofficeDocumentUploadsDrilldownData>> => {
    return client.get<BackofficeDocumentUploadsDrilldownData>(
        `/analytics/v2/backoffice-document-uploads/${agentId}`,
        { params: buildParams(from, to, serviceProviderId) }
    );
};

// ============================================================
// Document Status Moves (Case Status Tracking)
// ============================================================

/** Summary: document status changes per person */
export const getDocumentStatusMoves = (
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<DocumentStatusMovesSummaryData>> => {
    return client.get<DocumentStatusMovesSummaryData>('/analytics/v2/document-status-moves', {
        params: buildParams(from, to, serviceProviderId),
    });
};

/** Drilldown: per-document status changes for one person */
export const getDocumentStatusMovesByPerson = (
    personId: number,
    from?: string,
    to?: string,
    serviceProviderId?: string
): Promise<AxiosResponse<DocumentStatusMovesDrilldownData>> => {
    return client.get<DocumentStatusMovesDrilldownData>(
        `/analytics/v2/document-status-moves/${personId}`,
        { params: buildParams(from, to, serviceProviderId) }
    );
};
